import dayjs from 'dayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { MAX_NOTE_CHAR, DateTimeFormats, FormMode } from '@ge/models/constants';
import { Capability } from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { DelayDialog } from '@ge/shared/components/tasks/task-delay-dialog';
import { isActiveDelays } from '@ge/shared/util/time-date';

import active_delay from './active_delay.svg';
import collapse from './collapse.svg';
import expand from './expand.svg';

const DelayContainer = styled.div`
  background-color: #c5d2de;
  outline: 3px dashed #ffdd99;
  margin: 10px;
  margin-right: 20px;
  border-radius: 4px;
  height: auto;
  max-height: 300px;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    width: 6px;
    border-radius: 1px;
    background-color: rgba(12, 15, 18, 0.24);
  }
  &::-webkit-scrollbar-thumb {
    height: 100px;
    border: 2px solid transparent;
    background-clip: content-box;
    border-radius: 2.5px;
    background-color: ${(props) => props.theme.table.fixedBorderColor};
  }
`;

const StyledNewTaskDialog = styled.div`
  transition-property: max-height;
  transition-duration: 300ms;
  overflow: hidden;
  background-color: #c5d2de;
  .delay-row:nth-child(odd) {
    background-color: #b9c5d1;
  }
`;

const ToggleDelay = styled.span`
  color: #1e6978;
  font-size: 12px;
  font-weight: 600;
  line-height: 13.2px;
  letter-spacing: 0.5px;
  cursor: pointer;
`;

const DelayHeader = styled.h3`
  color: #424242;
  font-size: 11.8px;
  font-weight: 600;
  line-height: 13.2px;
  letter-spacing: 0.5px;
  font-family: Museo Sans;
`;

const DelayEnd = styled.span`
  color: #4d4d4d;
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
  letter-spacing: 0.5px;
`;

const DelayWrapper = styled.div`
  padding: 10px 10px 12px 20px;
`;

const DelayFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 10px 7px 18px;
  position: sticky;
  background-color: #c5d2de;
  bottom: 0;
`;

const DelayCount = styled.span`
  background: #5e6d7b;
  padding: 4px 8px;
  border-radius: 5px;
  margin-right: 5px;
  font-size: 12px;
`;

const HeaderWrapper = styled.span`
  display: flex;
  justify-content: space-between;
`;
const StyledNote = styled.div`
  white-space: pre-wrap;
  color: #4d4d4d;
  font-size: 11px;
  font-weight: 300;
  padding: 8px 0 3px;
  line-height: 15px;
  letter-spacing: 0.7px;
`;

const ShowMoreButton = styled.span`
  display: inline-block;
  cursor: pointer;
  color: ${(props) => props.theme.select.primaryBorder};
  text-transform: lowercase;
`;

const ShowMoreIcon = styled(Icon).attrs((props) => ({
  size: 10,
  icon: Icons.CHEVRON,
  color: props.theme.select.primaryBorder,
}))`
  margin: 2px;
  font-weight: bold;
`;

const TaskIcon = styled(Icon).attrs((props) => ({
  color: props.theme.themeSelector.headerColor,
}))`
  cursor: pointer;
`;

export const TaskDelayDetails = (task) => {
  const { t } = useTranslation(['entity-details']);
  const { delayDetails } = task.task;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMore, setShowMore] = useState([]);
  const [filteredDelays, setFilteredDelays] = useState([]);
  const [showAddDelay, setShowAddDelay] = useState(false);
  const [mode, setMode] = useState(FormMode.CREATE);
  const [currentDelay, setCurrentDelay] = useState(null);
  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((isExpanded) => !isExpanded);
  }, []);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      taskDelayReason: '',
      taskDelayTypes: '',
      delayStartDate: '',
      delayEndDate: '',
      delayNote: '',
    },
  });

  useEffect(() => {
    if (delayDetails?.length) {
      const delays = [];
      delayDetails.forEach((e, i) => {
        if (isActiveDelays(e.endDate)) delays.push({ ...e, index: i });
      });
      delays.sort(function (a, b) {
        return new Date(b.addedOn) - new Date(a.addedOn);
      });
      setFilteredDelays(delays);
      const hideShow = delays.map(() => false);
      setShowMore(hideShow);
    } else {
      setFilteredDelays([]);
    }
  }, [delayDetails]);

  const showMoreLess = (index) => {
    if (showMore[index]) {
      return (
        <>
          <ShowMoreIcon rotate={-180} /> {t('table.show_less', 'show less')}
        </>
      );
    } else {
      return (
        <>
          <span className="ellipsis">{'... '}</span>
          <ShowMoreIcon /> {t('table.show_more', 'show more')}
        </>
      );
    }
  };

  const toggleShowMore = useCallback(
    (index) => {
      setShowMore((prevState) => {
        let prevValue = [...prevState];
        prevValue[index] = !prevValue[index];
        return prevValue;
      });
    },
    [setShowMore],
  );

  const saveDelay = useCallback(() => {
    setShowAddDelay(false);
  }, []);

  const handleCloseDelayDialog = useCallback(() => setShowAddDelay(false), [setShowAddDelay]);

  const getNotes = (noteData, index) => {
    return (
      <StyledNote>
        {showMore[index] ? `${noteData} ` : `${noteData.slice(0, MAX_NOTE_CHAR)} `}
        {noteData?.length > MAX_NOTE_CHAR && (
          <ShowMoreButton onClick={() => toggleShowMore(index)}>
            {showMoreLess(index)}
          </ShowMoreButton>
        )}
      </StyledNote>
    );
  };

  const handleEditDelay = (delay, index) => {
    let setData = {};
    if (delay) {
      setData = {
        taskDelayReason: delay?.reason,
        taskDelayTypes: delay?.type,
        delayStartDate: delay?.startDate ? new Date(delay?.startDate) : null,
        delayEndDate: delay?.endDate && new Date(delay?.endDate),
        delayNote: delay?.notes,
      };
    } else {
      setData = {};
    }
    methods.reset(setData);
    setShowAddDelay(true);
    setCurrentDelay({ delay, index });
    setMode(FormMode.EDIT);
  };

  const generateDelayCard = (task, index, rowColor) => {
    const { reasonDesc, type, notes, endDate, index: delayIndex } = task;
    const formatedDate = dayjs(endDate).format(DateTimeFormats.DATEPICKER_CUSTOM_DATE);
    const endDateNew = dayjs(formatedDate).isValid() ? formatedDate : '-';
    const reason = reasonDesc?.toUpperCase() + (type ? ' - ' + type : '')?.toUpperCase();
    return (
      <DelayWrapper className={rowColor && 'delay-row'}>
        <HeaderWrapper>
          <DelayHeader>
            <img src={active_delay} alt="active_delay" /> DELAY: {reason}
          </DelayHeader>

          <AuthRender
            capability={Capability.FIELD_TASKS}
            create
            description="Add Delay"
            siteLevel={false}
          >
            <div onClick={() => handleEditDelay(task, delayIndex)}>
              <TaskIcon size={13} icon={Icons.PENCIL} />
            </div>
          </AuthRender>
        </HeaderWrapper>
        {getNotes(notes, index)}
        <DelayEnd>ENDS : {endDateNew}</DelayEnd>
      </DelayWrapper>
    );
  };

  const delayCardWrapper = () => {
    let values = [];
    values.push(generateDelayCard(filteredDelays[0], 0, false));
    if (filteredDelays.slice(1).length)
      values.push(
        <StyledNewTaskDialog style={{ maxHeight: isExpanded ? '1000px' : '0px' }}>
          {filteredDelays.slice(1).map((e, i) => generateDelayCard(e, i + 1, true))}
        </StyledNewTaskDialog>,
      );
    return values;
  };

  if (!filteredDelays?.length) return null;
  return (
    <DelayContainer>
      {delayCardWrapper()}
      {filteredDelays.length - 1 >= 1 && (
        <DelayFooter>
          <ToggleDelay onClick={toggleIsExpanded}>SEE {isExpanded ? 'LESS' : 'ALL'}</ToggleDelay>
          <div>
            {!isExpanded && <DelayCount>{filteredDelays.length - 1}</DelayCount>}
            <ToggleDelay onClick={toggleIsExpanded}>
              <img src={isExpanded ? collapse : expand} alt="expand" />
            </ToggleDelay>
          </div>
        </DelayFooter>
      )}
      {showAddDelay && (
        <FormProvider {...methods}>
          <DelayDialog
            task={task.task}
            onConfirm={saveDelay}
            onClose={handleCloseDelayDialog}
            methods={methods}
            mode={mode}
            currentDelay={currentDelay}
          />
        </FormProvider>
      )}
    </DelayContainer>
  );
};
export default TaskDelayDetails;

TaskDelayDetails.propTypes = {
  task: PropTypes.instanceOf(Object).isRequired,
};
